import { useEffect, useState } from "react";
import { dictationMissing, DOC_LABEL, type CustomerInfo, type DictationDraft, type DictationDraftInfo, type DictationType, type DocumentDetail } from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { eurosToCents } from "./Customers";
import { useRecorder } from "./NotesCalendar";
import { Icon, Message, Sheet } from "./ui";
import "./customers.css";
import "./dictation.css";

interface LineForm {
  name: string;
  quantity: string;
  unit: string;
  price: string;
  vatRate: 0 | 7 | 19;
  itemId: string | null;
  priceFrom: "diktat" | "artikel" | null;
}

const centsToText = (c: number) => (c / 100).toFixed(2).replace(".", ",");
const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const toNumber = (t: string) => {
  const n = Number(t.replace(/\./g, "").replace(",", "."));
  return t.trim() !== "" && Number.isFinite(n) && n > 0 ? n : null;
};

const toForm = (d: DictationDraft): LineForm[] =>
  d.lines.map((l) => ({ name: l.name, quantity: String(l.quantity).replace(".", ","), unit: l.unit, price: l.unitPriceCents === null ? "" : centsToText(l.unitPriceCents), vatRate: l.vatRate, itemId: l.itemId, priceFrom: l.priceFrom }));

function fromForm(type: DictationType, customerId: string, heard: string | null, lines: LineForm[]): Omit<DictationDraft, "text"> {
  return {
    type,
    customerId: customerId || null,
    customerHeard: heard,
    lines: lines.map((l) => ({
      name: l.name.trim(),
      quantity: toNumber(l.quantity) ?? 1,
      unit: l.unit.trim() || "Stk",
      unitPriceCents: l.price.trim() === "" ? null : eurosToCents(l.price),
      vatRate: l.vatRate,
      itemId: l.itemId,
      priceFrom: l.priceFrom,
    })),
  };
}

/** Diktat oder getippter Text wird zum Beleg-Entwurf. Was fehlt, ist markiert; speichern geht immer. */
export function DictationSheet(props: { onClose: () => void; onCreated: (id: string) => void }) {
  const [speech, setSpeech] = useState<boolean | null>(null);
  const [saved, setSaved] = useState<DictationDraftInfo[]>([]);
  const [text, setText] = useState("");
  const [current, setCurrent] = useState<DictationDraftInfo | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const recorder = useRecorder();

  useEffect(() => {
    api.get<{ speech: boolean }>("/api/dictation/status").then((s) => setSpeech(s.speech)).catch((err) => setError(errorText(err)));
    api.get<DictationDraftInfo[]>("/api/dictation/drafts").then(setSaved).catch(() => undefined);
  }, []);

  // Fertige Aufnahme zur eigenen Spracherkennung schicken und den Text anhängen.
  useEffect(() => {
    if (recorder.state !== "done" || !recorder.result) return;
    const { blob } = recorder.result;
    recorder.reset();
    setBusy(true);
    setNotice("Ihre Aufnahme wird in Text umgewandelt. Das dauert einen Moment.");
    const form = new FormData();
    form.append("audio", blob, "aufnahme");
    upload<{ text: string }>("/api/dictation/transcribe", form)
      .then((r) => setText((t) => (t.trim() ? `${t.trim()} ${r.text}` : r.text)))
      .catch((err) => setError(errorText(err)))
      .finally(() => {
        setBusy(false);
        setNotice("");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.state]);

  async function makeDraft() {
    setError("");
    setBusy(true);
    try {
      setCurrent(await api.post<DictationDraftInfo>("/api/dictation/drafts", { text }));
      setText("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (current) {
    return (
      <Sheet title="Entwurf aus Ihrem Diktat" onClose={props.onClose}>
        <DraftEditor
          key={current.id}
          info={current}
          onBack={() => {
            setCurrent(null);
            api.get<DictationDraftInfo[]>("/api/dictation/drafts").then(setSaved).catch(() => undefined);
          }}
          onCreated={props.onCreated}
        />
      </Sheet>
    );
  }

  return (
    <Sheet title="Per Sprache oder Text erstellen" onClose={props.onClose}>
      <div className="dic">
        <p className="muted-s">
          Sagen oder schreiben Sie, was angeboten werden soll, zum Beispiel: <i>„Angebot an Herrn Sülze, Stundenbasis 8 Stunden, Installationsrohr 32 Euro.“</i> HandVero macht daraus einen
          Entwurf. Kunde und Preise werden aus Ihren gespeicherten Daten genommen. Was fehlt, bleibt als „noch ausfüllen“ markiert.
        </p>
        {speech && (
          <div className="recorder">
            {recorder.state === "recording" ? (
              <>
                <div className="rec-pulse" aria-hidden="true" />
                <b className="rec-time">{clock(recorder.elapsed)}</b>
                <span className="muted-s">Ich höre zu. Zum Beenden tippen.</span>
                <button type="button" className="rec-btn stop" onClick={recorder.stop} aria-label="Aufnahme beenden">
                  <Icon name="stop" />
                </button>
              </>
            ) : (
              <>
                <span className="muted-s">{busy ? "Einen Moment …" : "Zum Diktieren tippen"}</span>
                <button type="button" className="rec-btn" disabled={busy} onClick={() => void recorder.start()} aria-label="Diktat starten">
                  <Icon name="mic" />
                </button>
              </>
            )}
            <Message>{recorder.error}</Message>
          </div>
        )}
        {speech === false && <p className="muted-s">Das Diktieren per Sprache ist auf diesem Server noch nicht eingerichtet. Tippen Sie den Text ein, der Entwurf funktioniert genauso.</p>}
        <label className="dic-text">
          Ihr Text (Sie können ihn vor dem Erstellen noch ändern)
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={4000} placeholder="Angebot an Frau Meier, 6 Stunden, 10 Meter Installationsrohr …" />
        </label>
        <Message kind="info">{notice}</Message>
        <Message>{error}</Message>
        <div className="cu-buttons">
          <button className="dbtn ghost" type="button" onClick={props.onClose}>
            Abbrechen
          </button>
          <button className="dbtn" disabled={busy || !text.trim()} onClick={() => void makeDraft()}>
            Entwurf erstellen
          </button>
        </div>
        {saved.length > 0 && (
          <div className="dic-saved">
            <b>Gespeicherte Entwürfe</b>
            {saved.map((d) => (
              <button key={d.id} type="button" className="dic-saved-row" onClick={() => setCurrent(d)}>
                <span>{d.text.length > 90 ? `${d.text.slice(0, 90)} …` : d.text}</span>
                <small className={d.missing.length ? "dic-open" : "dic-ready"}>{d.missing.length ? `${d.missing.length} noch auszufüllen` : "bereit"}</small>
              </button>
            ))}
          </div>
        )}
      </div>
    </Sheet>
  );
}

function DraftEditor(props: { info: DictationDraftInfo; onBack: () => void; onCreated: (id: string) => void }) {
  const { info } = props;
  const [type, setType] = useState<DictationType>(info.draft.type);
  const [customerId, setCustomerId] = useState(info.draft.customerId ?? "");
  const [lines, setLines] = useState<LineForm[]>(() => toForm(info.draft));
  const [customers, setCustomers] = useState<CustomerInfo[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<CustomerInfo[]>("/api/customers?role=customer").then(setCustomers).catch((err) => setError(errorText(err)));
  }, []);

  const body = fromForm(type, customerId, info.draft.customerHeard, lines);
  const missing = dictationMissing({ customerId: body.customerId, lines: body.lines });
  const badPrice = lines.some((l) => l.price.trim() !== "" && eurosToCents(l.price) === null);
  const set = (i: number, patch: Partial<LineForm>) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  async function save(quiet = false) {
    if (badPrice) throw new Error("Bitte geben Sie jeden Preis als Zahl an, zum Beispiel 65,50.");
    await api.put(`/api/dictation/drafts/${info.id}`, body);
    if (!quiet) setNotice("Der Entwurf ist gespeichert. Sie können ihn später weiter bearbeiten.");
  }

  async function run(fn: () => Promise<void>) {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const createDoc = () =>
    run(async () => {
      await save(true);
      const d = await api.post<DocumentDetail>("/api/documents", { type, customerId });
      await api.put(`/api/documents/${d.id}`, {
        issueDate: d.issueDate,
        dueDate: d.dueDate,
        lines: body.lines.map((l) => ({ name: l.name, description: null, quantity: l.quantity, unit: l.unit, unitPriceCents: l.unitPriceCents ?? 0, vatRate: l.vatRate, discountPercent: 0, isAlternative: false, markupPercent: 0, hideAdjustment: false })),
      });
      await api.del(`/api/dictation/drafts/${info.id}`);
      props.onCreated(d.id);
    });

  return (
    <div className="dic">
      <p className="dic-said">„{info.text}“</p>
      {missing.length > 0 ? (
        <p className="dic-todo" role="status">
          <b>Noch auszufüllen:</b> {missing.join(", ")}. Speichern können Sie den Entwurf trotzdem jederzeit.
        </p>
      ) : (
        <p className="dic-done" role="status">
          Alles ausgefüllt. Bitte prüfen Sie noch einmal die Angaben und legen Sie den Beleg an.
        </p>
      )}
      <div className="cu-form">
        <label>
          Art
          <select value={type} onChange={(e) => setType(e.target.value as DictationType)}>
            {(["quote", "order", "invoice"] as const).map((t) => (
              <option key={t} value={t}>
                {DOC_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className={customerId ? undefined : "dic-missing"}>
          Kunde {!customerId && <em className="dic-tag">noch ausfüllen</em>}
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Bitte wählen …</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.number} · {c.name}
                {c.city ? `, ${c.city}` : ""}
              </option>
            ))}
          </select>
          {!customerId && info.draft.customerHeard && <small className="muted-s">Verstanden: „{info.draft.customerHeard}“. Wählen Sie den Kunden aus der Liste oder legen Sie ihn zuerst unter „Kunden“ an.</small>}
        </label>
      </div>
      <div className="dic-lines">
        {lines.length === 0 && <p className="muted-s">Es wurde keine Position erkannt. Fügen Sie unten eine hinzu.</p>}
        {lines.map((l, i) => (
          <div key={i} className="dic-line">
            <label className="dic-name">
              Position
              <input value={l.name} onChange={(e) => set(i, { name: e.target.value })} />
            </label>
            <label>
              Menge
              <input value={l.quantity} inputMode="decimal" onChange={(e) => set(i, { quantity: e.target.value })} />
            </label>
            <label>
              Einheit
              <input value={l.unit} onChange={(e) => set(i, { unit: e.target.value })} />
            </label>
            <label className={l.price.trim() === "" ? "dic-missing" : undefined}>
              Preis netto (€) {l.price.trim() === "" && <em className="dic-tag">noch ausfüllen</em>}
              <input value={l.price} inputMode="decimal" placeholder="65,50" onChange={(e) => set(i, { price: e.target.value, priceFrom: null })} />
              {l.priceFrom === "artikel" && l.price && <small className="muted-s">aus Ihrem Artikelstamm</small>}
            </label>
            <button type="button" className="btn-text" onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))} aria-label="Position entfernen">
              Entfernen
            </button>
          </div>
        ))}
        <button type="button" className="dbtn ghost" onClick={() => setLines((ls) => [...ls, { name: "", quantity: "1", unit: "Stk", price: "", vatRate: 19, itemId: null, priceFrom: null }])}>
          Position hinzufügen
        </button>
      </div>
      <Message kind="ok">{notice}</Message>
      <Message>{error}</Message>
      <div className="cu-buttons">
        <button className="dbtn ghost" type="button" onClick={props.onBack}>
          Zurück
        </button>
        <button
          className="dbtn ghost"
          type="button"
          disabled={busy}
          onClick={() => run(async () => { await api.del(`/api/dictation/drafts/${info.id}`); props.onBack(); })}
        >
          Verwerfen
        </button>
        <button className="dbtn ghost" type="button" disabled={busy} onClick={() => void run(() => save())}>
          Entwurf speichern
        </button>
        <button className="dbtn" type="button" disabled={busy || missing.length > 0 || badPrice} onClick={() => void createDoc()} title={missing.length ? "Erst alles ausfüllen" : undefined}>
          {DOC_LABEL[type]} anlegen
        </button>
      </div>
    </div>
  );
}
